import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Usuario, Empresa, UserEmpresa, StoreGroup } from '../types';
import { logger } from '../utils/logger';
import { mapSignupAuthError } from '../lib/signupRules';

/** Internal: login failed because Auth requires confirmed email (see catch handler). */
const LOGIN_EMAIL_UNCONFIRMED_PREFIX = '__EMAIL_UNCONFIRMED__::';

/** Normalize email for Auth + DB lookups (avoids mismatches when usuarios.email is lowercase). */
function normalizeLoginEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isEmailNotConfirmedAuthMessage(msg: string): boolean {
  const m = msg.toLowerCase();
  return (
    m.includes('email not confirmed') ||
    m.includes('email address not confirmed') ||
    m.includes('confirm your email')
  );
}

/** User-facing Spanish messages for common Supabase Auth errors. */
function mapAuthLoginError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid credentials')) {
    return 'Correo o contraseña incorrectos. Si no recuerdas la contraseña, usa «¿Olvidaste tu contraseña?».';
  }
  if (m.includes('email not confirmed') || m.includes('email address not confirmed')) {
    return 'Tu correo aún no está confirmado. Usa «Reenviar enlace» en la pantalla de inicio de sesión o revisa spam.';
  }
  if (m.includes('too many requests') || m.includes('rate limit')) {
    return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  }
  if (m.includes('user is banned') || m.includes('banned')) {
    return 'Esta cuenta no puede iniciar sesión. Contacta a soporte.';
  }
  if (raw.startsWith('Authentication failed:')) {
    return raw.replace(/^Authentication failed:\s*/i, '').trim() || 'No se pudo iniciar sesión.';
  }
  return raw;
}

/** Supabase rows use created_at; frontend Empresa type uses createdAt Date. */
function empresaRowToFrontendDates(empresa: {
  created_at?: string;
  updated_at?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}) {
  const parseDate = (val: any): Date => {
    if (!val) return new Date();
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date() : d;
  };
  return {
    createdAt: parseDate(empresa?.created_at ?? empresa?.createdAt),
    updatedAt: parseDate(empresa?.updated_at ?? empresa?.updatedAt),
  };
}

function empresaRowToFrontend(row: any): any {
  return {
    ...row,
    ...empresaRowToFrontendDates(row ?? {}),
  };
}

// Updated validation function for multi-company
const validateUserSetup = (usuario: Usuario) => {
  const issues = [];
  
  // Check if user has any companies
  if (!usuario.userEmpresas || usuario.userEmpresas.length === 0) {
    issues.push("no_companies");
  }
  
  // Check if user has a current company selected
  if (!usuario.currentEmpresa) {
    issues.push("no_current_company");
  }
  
  return {
    isComplete: issues.length === 0,
    issues
  };
};

/** Map a usuarios row from DB to Usuario type */
const rowToUsuario = (row: {
  id: string;
  nombre?: string;
  email?: string;
  rol?: string;
  tipo_usuario?: string;
  rol_id?: string | null;
  empresa_id?: string | null;
  activo?: boolean;
  created_at?: string;
  updated_at?: string;
}): Usuario => ({
  id: row.id,
  nombre: row.nombre ?? '',
  email: row.email ?? '',
  rol: row.rol ?? 'OWNER',
  tipo_usuario: row.tipo_usuario ?? 'ADMIN',
  tipoUsuario: row.tipo_usuario ?? 'ADMIN',
  rol_id: row.rol_id ?? null,
  empresaId: row.empresa_id ?? '',
  activo: row.activo !== false,
  fechaCreacion: new Date(row.created_at ?? Date.now()),
  fechaActualizacion: new Date(row.updated_at ?? row.created_at ?? Date.now()),
});

interface AuthState {
  // Existing properties
  usuario: Usuario | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userSetupValidation: {
    isComplete: boolean;
    issues: string[];
  } | null;

  // NEW: Multi-company properties
  userEmpresas: UserEmpresa[];
  currentEmpresa: Empresa | null;
  isLoadingEmpresas: boolean;
  showCompanySelector: boolean;

  /** Supabase Auth user (set on login); used when usuario is created after empresa setup */
  authUser: { id: string; email?: string; user_metadata?: Record<string, unknown> } | null;

  // NEW: Simple executive dashboard properties
  currentStoreGroup: StoreGroup | null;
  userStoreGroups: StoreGroup[];
  currentAccessLevel: 'store' | 'group' | 'executive';
  availableEmpresas: Empresa[];
  isExecutiveView: boolean;
  selectedEmpresas: string[];
  selectedTimeRange: string;
  globalMetrics: any | null;
  storePerformance: any[];
  isLoadingMetrics: boolean;

  // Existing actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: Partial<Usuario>, password: string) => Promise<void>;
  setUsuario: (usuario: Usuario) => void;
  setToken: (token: string) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  checkSession: () => Promise<void>;
  validateUserSetup: () => void;

  // NEW: Multi-company actions
  fetchUserEmpresas: () => Promise<void>;
  switchEmpresa: (empresaId: string) => Promise<void>;
  setCurrentEmpresa: (empresa: Empresa) => void;
  createNewEmpresa: (empresaData: Partial<Empresa>) => Promise<Empresa>;

  /** Update current usuario with empresa_id and rol_id (Admin) after first empresa is created */
  createUserAfterEmpresaSetup: (empresaId: string, nombreUsuario?: string) => Promise<Usuario | null>;

  // NEW: Simple executive dashboard actions
  fetchStoreGroups: () => Promise<void>;
  switchToExecutiveView: () => void;
  switchToStoreView: (empresaId: string) => void;
  setCurrentStoreGroup: (storeGroup: StoreGroup) => void;
  setSelectedEmpresas: (empresaIds: string[]) => void;
  setSelectedTimeRange: (timeRange: string) => void;
  fetchGlobalMetrics: (empresaIds: string[], timeRange: string) => Promise<void>;
  fetchStorePerformance: (empresaIds: string[], timeRange: string) => Promise<void>;

  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (newPassword: string) => Promise<void>;
  /** Consumes recovery token_hash once; after success, call resetPassword (retry-safe). */
  verifyRecoveryOtp: (tokenHash: string) => Promise<void>;

  /** Set when signUp succeeds but email must be confirmed (no session yet). */
  registrationEmailPendingVerification: string | null;
  dismissRegistrationPending: () => void;

  /** After failed login when Supabase says email is not confirmed — enables «Reenviar enlace». */
  authResendHintEmail: string | null;

  /** Resend Supabase signup confirmation email (only works if SMTP is configured in the Supabase project). */
  resendSignupEmail: (email: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state - START with isLoading: true to prevent premature redirects during checkSession
      usuario: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      registrationEmailPendingVerification: null,
      authResendHintEmail: null,
  userSetupValidation: null,
  userEmpresas: [],
  currentEmpresa: null,
  isLoadingEmpresas: false,
  showCompanySelector: false,
  authUser: null,

  // Simple executive dashboard initial state
  currentStoreGroup: null,
  userStoreGroups: [],
  currentAccessLevel: 'store',
  availableEmpresas: [],
  isExecutiveView: false,
  selectedEmpresas: [],
  selectedTimeRange: '30d',
  globalMetrics: null,
  storePerformance: [],
  isLoadingMetrics: false,

  // UPDATED LOGIN with multi-company support
      login: async (email: string, password: string) => {
        // SECURITY: Never log passwords or credentials
        set({ isLoading: true, error: null, authResendHintEmail: null });

        try {
          const normalizedEmail = normalizeLoginEmail(email);
          if (!normalizedEmail) {
            throw new Error('Ingresa un correo electrónico válido.');
          }

          // Import supabase here to avoid circular dependency
          const { supabase } = await import('../lib/supabase');
          
          // SECURITY: Never log Supabase client (contains keys) or auth endpoint
          
          // Step 1: Authenticate with Supabase Auth (checks auth.users table)
          const authPromise = supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password: password
          });
          
          const { data: authData, error: authError } = await authPromise;

          if (authError) {
            // SECURITY: Only log error message, not full error object (may contain sensitive data)
            const errorMessage = authError.message || 'Authentication failed';

            if (isEmailNotConfirmedAuthMessage(errorMessage)) {
              throw new Error(`${LOGIN_EMAIL_UNCONFIRMED_PREFIX}${normalizedEmail}`);
            }
            
            // Invalid credentials: show a clear message and point to Forgot password
            if (errorMessage.includes('Invalid login credentials') || errorMessage.includes('Invalid credentials')) {
              const { data: usuarioCheck, error: usuarioCheckError } = await supabase
                .from('usuarios')
                .select('id, email, nombre, auth_id')
                .eq('email', normalizedEmail)
                .maybeSingle();
              // If email exists in usuarios, they may have an Auth account (wrong password) or were invited (no Auth yet). Either way, suggest Forgot password first.
              if (usuarioCheck && !usuarioCheckError) {
                throw new Error('Correo o contraseña incorrectos. Usa «¿Olvidaste tu contraseña?» si no la recuerdas. Si no tienes cuenta, regístrate o contacta al administrador.');
              }
            }
            
            throw new Error(mapAuthLoginError(errorMessage));
          }

          if (!authData.user) {
            throw new Error('No se pudo autenticar el usuario');
          }

          // SECURITY: Never log authData (contains sensitive session tokens)

      // Step 2: Get user record from usuarios table
          let userRecord: any = null;
          const { data: existingUser, error: userError } = await supabase
            .from('usuarios')
            .select('id, email, nombre, apellido, telefono, tipo_usuario, rol, rol_id, activo, created_at, updated_at')
            .eq('email', normalizedEmail)
            .maybeSingle();

          if (userError && userError.code !== 'PGRST116') {
            logger.error('❌ Error fetching user record:', userError);
          }

          // If user doesn't exist in usuarios table, create a minimal stub so they can create their first empresa
          // Use rol 'OWNER' to satisfy any remaining CHECK; empresa_id/rol_id set after empresa creation
          if (!existingUser) {
            logger.debug('👤 User record not found in usuarios table, creating stub...');
            const { data: newUser, error: createError } = await supabase
              .from('usuarios')
              .upsert(
                {
                  auth_id: authData.user.id,
                  email: normalizedEmail,
                  nombre: authData.user.user_metadata?.nombre || normalizedEmail.split('@')[0] || 'Usuario',
                  apellido: authData.user.user_metadata?.apellido || '',
                  telefono: authData.user.user_metadata?.telefono || '',
                  tipo_usuario: 'ADMIN',
                  rol: 'OWNER',
                  activo: true,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                },
                { onConflict: 'auth_id', ignoreDuplicates: false }
              )
              .select()
              .single();

            if (createError || !newUser) {
              // Retry fetch in case of conflict (record already exists)
              const { data: retryUser } = await supabase
                .from('usuarios')
                .select('id, email, nombre, apellido, telefono, tipo_usuario, rol, rol_id, activo, created_at, updated_at')
                .eq('auth_id', authData.user.id)
                .maybeSingle();

              if (retryUser) {
                userRecord = retryUser;
                logger.debug('✅ Existing user record recovered after upsert conflict');
              } else {
                logger.error('❌ Error creating user record:', createError);
                await supabase.auth.signOut();
                const hint = createError?.message
                  ? ` (${createError.message})`
                  : '';
                throw new Error(
                  `No se pudo crear o vincular tu perfil de usuario${hint}. Vuelve a intentar o contacta a soporte.`
                );
              }
            } else {
              userRecord = newUser;
              logger.debug('✅ User stub created successfully');
            }
          } else {
            userRecord = existingUser;
          }

      // Step 3: Fetch all user's companies from empresas table (where user is owner OR member)
      // Use userRecord.id if available, otherwise use authData.user.id
      const _userId = userRecord?.id || authData.user.id;
      // Fetching user companies
      
      // IMPORTANT: Only search using userRecord.id (from usuarios table), not authData.user.id
      // Because empresas.owner_id references usuarios.id, not auth.users.id
      if (!userRecord?.id) {
        logger.warn('⚠️ No userRecord.id found - cannot fetch empresas');
        await supabase.auth.signOut();
        throw new Error(
          'No se encontró tu perfil de usuario en la base de datos. Contacta a soporte para revisar tu cuenta.'
        );
      }

      const usuarioId = userRecord.id;

      // Get empresas where user is owner
      const { data: empresasOwner, error: ownerError } = await supabase
        .from('empresas')
        .select('*')
        .eq('owner_id', usuarioId);

      if (ownerError) {
        console.error('❌ Error fetching empresas (owner):', ownerError);
      }

      // Get empresas where user is a member via usuarios_empresa
      // Fetch BOTH empresa_id AND role from usuarios_empresa
      const { data: userEmpresasData, error: userEmpresasError } = await supabase
        .from('usuarios_empresa')
        .select('empresa_id, role')
        .eq('usuario_id', usuarioId);

      if (userEmpresasError) {
        // This is OK if user is only owner (not a member of any other empresas)
        // Error is expected if usuarios_empresa table doesn't exist or user has no memberships
      }

      // Create a map of empresa_id -> role from usuarios_empresa
      const empresaRoleMap = new Map<string, string>();
      if (userEmpresasData) {
        userEmpresasData.forEach((ue: any) => {
          if (ue.empresa_id && ue.role) {
            empresaRoleMap.set(ue.empresa_id, ue.role);
          }
        });
      }

      // Combine both lists (owner and member empresas)
      const empresasOwnerList = empresasOwner || [];
      let empresasMemberList: any[] = [];
      
      // If we have member empresa IDs, fetch the empresas separately
      if (userEmpresasData && userEmpresasData.length > 0) {
        const memberEmpresaIds = userEmpresasData
          .map(ue => ue.empresa_id)
          .filter((id): id is string => !!id && typeof id === 'string');
        
        if (memberEmpresaIds.length > 0) {
          const { data: memberEmpresas, error: memberEmpresasError } = await supabase
            .from('empresas')
            .select('id, nombre, descripcion, direccion, ciudad, pais, telefono, email, slug, owner_id, activa, created_at, updated_at, configuracion, emprendedores_de_fe_activo')
            .in('id', memberEmpresaIds);
          
          if (!memberEmpresasError && memberEmpresas) {
            empresasMemberList = memberEmpresas;
          }
        }
      }
      
      // Merge and deduplicate by empresa.id
      const empresasMap = new Map<string, any>();
      empresasOwnerList.forEach(emp => empresasMap.set(emp.id, emp));
      empresasMemberList.forEach(emp => {
        if (emp && !empresasMap.has(emp.id)) {
          empresasMap.set(emp.id, emp);
        }
      });

      let empresasData = Array.from(empresasMap.values());
      // Found empresas (owner + member): empresasData.length

      // Convert empresas to userEmpresas format for compatibility
      // Use actual role from usuarios_empresa or 'owner' if user is the owner
      const _empresas = empresasData || [];
      
      // All empresas found are already authorized (owner or member via usuarios_empresa)
      const authorizedEmpresas = empresasData;
      
      const userEmpresas = authorizedEmpresas.map(empresa => {
        // Determine role: 'owner' if empresa.owner_id === usuarioId, otherwise use role from usuarios_empresa
        const isOwner = empresa.owner_id === usuarioId;
        const role = isOwner ? 'OWNER' : (empresaRoleMap.get(empresa.id) || 'EMPLEADO');
        
        return {
          id: `temp-${empresa.id}`,
          user_id: authData.user.id,
          empresa_id: empresa.id,
          role: role as UserEmpresa['role'],
          created_at: new Date().toISOString(),
          empresa: empresa
        };
      });
      // SECURITY: Never log full empresa data (contains sensitive configuration)
      // Found companies: userEmpresas.length

      // Step 4: Smart company selection logic
      let currentEmpresa: Empresa | null = null;
      let showCompanySelector = false;
      
      if (userEmpresas.length === 0) {
        // No companies found - user needs to create one
      } else if (userEmpresas.length === 1) {
        // Auto-select single company
        const primeraEmpresaUsuario = userEmpresas[0];
        if (primeraEmpresaUsuario?.empresa) {
          currentEmpresa = primeraEmpresaUsuario.empresa;
        }
      } else {
        // Multiple companies - check localStorage for last selected
        const lastSelectedEmpresaId = localStorage.getItem('lastSelectedEmpresaId');

        if (lastSelectedEmpresaId) {
          const lastSelected = userEmpresas.find(ue => ue.empresa.id === lastSelectedEmpresaId);
          if (lastSelected?.empresa) {
            currentEmpresa = lastSelected.empresa;
          } else {
            // Last selected not found, use first company as default
            const primeraEmpresaUsuario = userEmpresas[0];
            if (primeraEmpresaUsuario?.empresa) {
              currentEmpresa = primeraEmpresaUsuario.empresa;
            }
          }
        } else {
          // No last selected, use first company as default
          const primeraEmpresaUsuario = userEmpresas[0];
          if (primeraEmpresaUsuario?.empresa) {
            currentEmpresa = primeraEmpresaUsuario.empresa;
          }
        }

        // Only show selector if no empresa was found/selected
        // If we have a currentEmpresa, don't show the selector (user can use dropdown instead)
        if (!currentEmpresa) {
          showCompanySelector = true;
          logger.debug('🔄 Multiple companies found but no empresa selected, showing selector');
        } else {
          logger.debug('✅ Multiple companies found, empresa selected:', currentEmpresa.nombre);
        }
      }

      // Step 5: Create user object with multi-company data
          // CRITICAL: Use ONLY userRecord.id (usuarios.id), never authData.user.id
          // The usuarios.id is what's used in empresas.owner_id and usuarios_empresa.usuario_id
          if (!userRecord?.id) {
            console.error('❌ CRITICAL: userRecord.id is missing - cannot create usuario object');
            throw new Error('Falta el identificador de usuario. Contacta a soporte.');
          }

          const usuario: Usuario = {
        id: userRecord.id, // ALWAYS use usuarios.id, never auth.uid()
            nombre: userRecord.nombre || authData.user.user_metadata?.name || normalizedEmail.split('@')[0] || 'Usuario',
            email: normalizedEmail,
        empresa: currentEmpresa?.nombre || 'Sin Empresa',
            moneda: 'COP',
            zonaHoraria: 'America/Bogota',
            fechaRegistro: new Date(userRecord.created_at || authData.user.created_at || new Date()),
            ultimoAcceso: new Date(),
            rol: userRecord.rol || 'VENDEDOR', // Default to VENDEDOR, not ADMIN
            tipoUsuario: userRecord.tipo_usuario || 'EMPLEADO', // Default to EMPLEADO, not ADMIN
            tipo_usuario: userRecord.tipo_usuario || 'EMPLEADO',
            rol_id: userRecord.rol_id || null, // Include rol_id for RBAC
        empresaId: currentEmpresa?.id || 'sin-empresa',
            activo: userRecord.activo !== false,
            fechaCreacion: new Date(userRecord.created_at || authData.user.created_at || new Date()),
        fechaActualizacion: new Date(userRecord.updated_at || userRecord.created_at || authData.user.created_at || new Date()),
        // NEW: Multi-company properties
        userEmpresas: userEmpresas.map(ue => ({
          ...ue,
          empresa: empresaRowToFrontend(ue.empresa),
        })),
        currentEmpresa: currentEmpresa ? empresaRowToFrontend(currentEmpresa) : null,
          };

          console.log('Auth store: Setting authenticated user:', usuario);
      
      // Validate user setup
      const validation = validateUserSetup(usuario);
      console.log('🔍 User setup validation:', validation);

          set({
            usuario,
            token: authData.session?.access_token || 'demo-token-123',
            isAuthenticated: true,
            isLoading: false,
        error: null,
        registrationEmailPendingVerification: null,
        authResendHintEmail: null,
        userSetupValidation: validation,
        userEmpresas: usuario.userEmpresas,
        currentEmpresa: usuario.currentEmpresa,
        showCompanySelector: showCompanySelector,
        authUser: { id: authData.user.id, email: authData.user.email ?? undefined, user_metadata: authData.user.user_metadata },
          });

      try {
        const { flushPendingReferralAttribution } = await import('../lib/pendingReferralAttribution');
        await flushPendingReferralAttribution(supabase, usuarioId);
      } catch {
        /* non-fatal */
      }

      // Also sync with CompanyStore to keep both stores in sync
      if (currentEmpresa && userEmpresas.length > 0) {
        try {
          const { useCompanyStore } = await import('./useCompanyStore');
          const empresasFrontend = userEmpresas
            .filter(ue => ue.empresa)
            .map(ue => {
              const empresa = ue.empresa!;
              const fe = empresaRowToFrontend(empresa);
              return {
                id: empresa.id,
                nombre: empresa.nombre,
                descripcion: empresa.descripcion || undefined,
                direccion: empresa.direccion || '',
                ciudad: empresa.ciudad || undefined,
                pais: empresa.pais || undefined,
                telefono: empresa.telefono || '',
                email: empresa.email || '',
                slug: empresa.slug || '',
                logo: empresa.logo || empresa.configuracion?.branding?.logo || undefined,
                ownerId: empresa.owner_id || '',
                activa: empresa.activa !== undefined ? empresa.activa : true,
                createdAt: fe.createdAt.toISOString(),
                updatedAt: fe.updatedAt.toISOString()
              };
            });

          const currentFe = empresaRowToFrontend(currentEmpresa);
          const currentEmpresaFrontend = currentEmpresa ? {
            id: currentEmpresa.id,
            nombre: currentEmpresa.nombre,
            descripcion: currentEmpresa.descripcion || undefined,
            direccion: currentEmpresa.direccion || '',
            ciudad: currentEmpresa.ciudad || undefined,
            pais: currentEmpresa.pais || undefined,
            telefono: currentEmpresa.telefono || '',
            email: currentEmpresa.email || '',
            slug: currentEmpresa.slug || '',
            logo: currentEmpresa.logo || undefined,
            ownerId: currentEmpresa.owner_id || '',
            activa: currentEmpresa.activa !== undefined ? currentEmpresa.activa : true,
            createdAt: currentFe.createdAt.toISOString(),
            updatedAt: currentFe.updatedAt.toISOString()
          } : null;

          useCompanyStore.setState({
            empresas: empresasFrontend,
            currentEmpresa: currentEmpresaFrontend,
            loading: false
          });

          // CompanyStore synced from AuthStore after login
        } catch (syncError) {
          console.warn('⚠️ Error syncing CompanyStore:', syncError);
          // Don't fail login if sync fails
        }
      }

      // Login successful
      
        } catch (error) {
      // SECURITY: Only log error message, not full error object
      let errorMessage = error instanceof Error ? error.message : 'No se pudo iniciar sesión.';
      let authResendHintEmail: string | null = null;
      if (errorMessage.startsWith(LOGIN_EMAIL_UNCONFIRMED_PREFIX)) {
        authResendHintEmail = errorMessage.slice(LOGIN_EMAIL_UNCONFIRMED_PREFIX.length).trim() || null;
        errorMessage =
          'Tu correo aún no está verificado. Pulsa «Reenviar enlace de verificación» abajo o revisa spam. Si no recibes el correo, escribe a soporte.';
      }
        set({
          usuario: null,
          token: null,
          isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
        authResendHintEmail,
        userSetupValidation: null,
        userEmpresas: [],
        currentEmpresa: null,
        showCompanySelector: false,
      });
      // Re-throw the error so the calling code knows login failed
      throw error;
    }
  },

  // UPDATED REGISTER with multi-company support
      register: async (userData: Partial<Usuario>, password: string) => {
    console.log('📝 Starting registration process...');
        set({
          isLoading: true,
          error: null,
          registrationEmailPendingVerification: null,
          authResendHintEmail: null,
        });

        try {
          const normalizedEmail = normalizeLoginEmail(userData.email || '');
          if (!normalizedEmail) {
            throw new Error('Ingresa un correo electrónico válido.');
          }

          // Import supabase here to avoid circular dependency
          const { supabase } = await import('../lib/supabase');

          const emailRedirectTo =
            typeof window !== 'undefined' ? `${window.location.origin}/login` : undefined;

      // Step 1: Create Supabase auth user with metadata
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: normalizedEmail,
            password: password,
            options: {
              ...(emailRedirectTo ? { emailRedirectTo } : {}),
              data: {
                nombre: userData.nombre || normalizedEmail.split('@')[0],
                apellido: userData.apellido || '',
                telefono: userData.telefono || null,
              }
            }
      });

          if (authError) {
        console.error('❌ Auth registration error:', authError);
        throw new Error(mapSignupAuthError(authError.message));
          }

          if (!authData.user) {
        throw new Error('No se pudo crear la cuenta. Intenta de nuevo.');
      }

      console.log('✅ Auth user created:', authData.user.id);

      // Supabase: si "Confirmar correo" está activo, session es null hasta que abran el enlace
      if (!authData.session) {
        set({
          usuario: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          registrationEmailPendingVerification: normalizedEmail,
          authResendHintEmail: null,
          userSetupValidation: null,
          userEmpresas: [],
          currentEmpresa: null,
          showCompanySelector: false,
          authUser: null,
        });
        console.log('📧 Registro OK — pendiente confirmar correo');
        return;
      }

      console.log('✅ Sesión activa tras registro (confirmación de correo desactivada o ya confirmado)');

      const userEmpresa: UserEmpresa = {
        id: `temp-${Date.now()}`,
        user_id: authData.user.id,
        empresa_id: '',
        role: 'OWNER',
        created_at: new Date().toISOString(),
        empresa: undefined,
      };

      await new Promise(resolve => setTimeout(resolve, 500));
      
      let newUserRecord = null;
      let fetchError = null;
      let retries = 3;
      
      while (retries > 0 && !newUserRecord) {
        const { data, error } = await supabase
          .from('usuarios')
          .select('id, email, nombre, apellido, telefono, tipo_usuario, rol, rol_id, activo, created_at, updated_at')
          .eq('email', normalizedEmail)
          .maybeSingle();
        
        if (data) {
          newUserRecord = data;
          break;
        }
        
        fetchError = error;
        retries--;
        
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (!newUserRecord) {
        throw new Error(
          'No se pudo vincular tu cuenta con el perfil de usuario. Espera unos segundos e intenta iniciar sesión.'
        );
      }

      console.log('✅ Usuarios record found after registration:', newUserRecord.id);
      const normalizedPhone = userData.telefono?.trim();
      if (normalizedPhone && newUserRecord.telefono !== normalizedPhone) {
        const { data: patchedUser, error: patchPhoneError } = await supabase
          .from('usuarios')
          .update({ telefono: normalizedPhone, updated_at: new Date().toISOString() })
          .eq('id', newUserRecord.id)
          .select('id, email, nombre, apellido, telefono, tipo_usuario, rol, rol_id, activo, created_at, updated_at')
          .maybeSingle();
        if (patchPhoneError) {
          console.warn('⚠️ Could not persist telefono on usuarios:', patchPhoneError.message);
        } else if (patchedUser) {
          newUserRecord = patchedUser;
        }
      }

      const accessToken = authData.session.access_token;
      const usuario: Usuario = {
        id: newUserRecord.id,
        nombre: newUserRecord.nombre || userData.nombre || '',
        email: normalizedEmail,
        telefono: newUserRecord.telefono || userData.telefono || '',
        empresa: '',
        moneda: 'COP',
        zonaHoraria: 'America/Bogota',
        fechaRegistro: new Date(newUserRecord?.created_at || Date.now()),
        ultimoAcceso: new Date(),
        rol: newUserRecord?.rol || 'VENDEDOR',
        tipoUsuario: newUserRecord?.tipo_usuario || 'EMPLEADO',
        tipo_usuario: newUserRecord?.tipo_usuario || 'EMPLEADO',
        rol_id: newUserRecord?.rol_id || null,
        empresaId: '',
        activo: newUserRecord?.activo !== false,
        fechaCreacion: new Date(newUserRecord?.created_at || Date.now()),
        fechaActualizacion: new Date(newUserRecord?.updated_at || newUserRecord?.created_at || Date.now()),
        userEmpresas: [userEmpresa],
        currentEmpresa: null,
      };
          
      const validation = validateUserSetup(usuario);
      console.log('🔍 User setup validation:', validation);

      set({
        usuario,
        token: accessToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        registrationEmailPendingVerification: null,
        authResendHintEmail: null,
        userSetupValidation: validation,
        userEmpresas: [userEmpresa],
        currentEmpresa: null,
        showCompanySelector: false,
        authUser: { id: authData.user.id, email: authData.user.email ?? undefined, user_metadata: authData.user.user_metadata },
      });

      console.log('🎉 Registration successful!');

        } catch (error) {
      console.error('❌ Registration failed:', error);
          set({
        usuario: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'No se pudo completar el registro.',
        registrationEmailPendingVerification: null,
        authResendHintEmail: null,
        userSetupValidation: null,
        userEmpresas: [],
        currentEmpresa: null,
        showCompanySelector: false,
          });
          throw error instanceof Error ? error : new Error(String(error));
        }
      },

  // UPDATED CHECK SESSION with multi-company support
      checkSession: async () => {
        set({ isLoading: true });
        
        try {
          const { supabase } = await import('../lib/supabase');
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
        console.error('❌ Session check error:', error);
            set({ isLoading: false, isAuthenticated: false });
            return;
          }
          
      if (!session) {
        console.log('ℹ️ No active session');
            set({ isLoading: false, isAuthenticated: false });
            return;
          }
          
      logger.debug('✅ Active session found, fetching user data...');
          
      // Fetch user data from usuarios table using email (like in login)
      
      let userRecord: any = null;
      const { data: existingUser, error: userError } = await supabase
        .from('usuarios')
        .select('*')
        .eq('email', session.user.email)
        .maybeSingle();

      if (userError && userError.code !== 'PGRST116') {
        console.error('❌ Error fetching user record:', userError);
      }

      // If user doesn't exist in usuarios table, create stub (same as login - use OWNER)
      if (!existingUser) {
        logger.debug('👤 User record not found in usuarios table, creating stub...');
        const { data: newUser, error: createError } = await supabase
          .from('usuarios')
          .upsert(
            {
              auth_id: session.user.id,
              email: session.user.email || '',
              nombre: session.user.user_metadata?.nombre || session.user.email?.split('@')[0] || 'Usuario',
              apellido: session.user.user_metadata?.apellido || '',
              telefono: session.user.user_metadata?.telefono || '',
              tipo_usuario: 'ADMIN',
              rol: 'OWNER',
              activo: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            },
            { onConflict: 'auth_id', ignoreDuplicates: false }
          )
          .select()
          .single();

        if (createError || !newUser) {
          // 409/conflict means the record already exists — retry the fetch
          const { data: retryUser, error: retryError } = await supabase
            .from('usuarios')
            .select('*')
            .eq('auth_id', session.user.id)
            .maybeSingle();

          if (retryUser) {
            userRecord = retryUser;
            logger.debug('✅ User record recovered after upsert conflict');
          } else {
            console.error('❌ Could not create or recover user record:', createError, retryError);
            set({ isLoading: false, isAuthenticated: false, error: 'Error al cargar registro de usuario' });
            return;
          }
        } else {
          userRecord = newUser;
          logger.debug('✅ User record upserted successfully');
        }
      } else {
        userRecord = existingUser;
      }

      // Create user object from usuarios table data
      // CRITICAL: Use ONLY userRecord.id (usuarios.id), never auth.uid()
      const sessionUsuario = {
        id: userRecord.id, // ALWAYS use usuarios.id
        email: userRecord.email,
        nombre: userRecord.nombre,
        telefono: userRecord.telefono || '',
        fechaRegistro: new Date(userRecord.created_at),
        ultimoAcceso: new Date(),
        rol: userRecord.rol || 'VENDEDOR', // Default to VENDEDOR, not ADMIN
        tipoUsuario: userRecord.tipo_usuario || 'EMPLEADO', // Default to EMPLEADO, not ADMIN
        tipo_usuario: userRecord.tipo_usuario || 'EMPLEADO',
        rol_id: userRecord.rol_id || null, // Include rol_id for RBAC
        empresaId: 'sin-empresa',
        activo: userRecord.activo !== false,
        fechaCreacion: new Date(userRecord.created_at),
        fechaActualizacion: new Date(userRecord.updated_at || userRecord.created_at),
        userEmpresas: [],
        currentEmpresa: null
      };
        
      // Fetch all user's companies using usuarios.id (where user is owner OR member)
      logger.debug('🔄 Fetching user companies for usuario.id:', userRecord.id);
      
      const usuarioId = userRecord.id;

      // Get empresas where user is owner
      const { data: empresasOwner, error: ownerError } = await supabase
        .from('empresas')
        .select('*')
        .eq('owner_id', usuarioId);

      if (ownerError) {
        console.error('❌ Error fetching empresas (owner):', ownerError);
      }

      // Get empresas where user is a member via usuarios_empresa
      // Fetch BOTH empresa_id AND role from usuarios_empresa
      const { data: userEmpresasData, error: userEmpresasError } = await supabase
        .from('usuarios_empresa')
        .select('empresa_id, role')
        .eq('usuario_id', usuarioId);

      if (userEmpresasError) {
        // This is OK if user is only owner (not a member of any other empresas)
        // Error is expected if usuarios_empresa table doesn't exist or user has no memberships
      }

      // Create a map of empresa_id -> role from usuarios_empresa
      const empresaRoleMap = new Map<string, string>();
      if (userEmpresasData) {
        userEmpresasData.forEach((ue: any) => {
          if (ue.empresa_id && ue.role) {
            empresaRoleMap.set(ue.empresa_id, ue.role);
          }
        });
      }

      // Combine both lists (owner and member empresas)
      const empresasOwnerList = empresasOwner || [];
      let empresasMemberList: any[] = [];
      
      // If we have member empresa IDs, fetch the empresas separately
      if (userEmpresasData && userEmpresasData.length > 0) {
        const memberEmpresaIds = userEmpresasData
          .map(ue => ue.empresa_id)
          .filter((id): id is string => !!id && typeof id === 'string');
        
        if (memberEmpresaIds.length > 0) {
          const { data: memberEmpresas, error: memberEmpresasError } = await supabase
            .from('empresas')
            .select('id, nombre, descripcion, direccion, ciudad, pais, telefono, email, slug, owner_id, activa, created_at, updated_at, configuracion, emprendedores_de_fe_activo')
            .in('id', memberEmpresaIds);
          
          if (!memberEmpresasError && memberEmpresas) {
            empresasMemberList = memberEmpresas;
          }
        }
      }
      
      // Merge and deduplicate by empresa.id
      const empresasMap = new Map<string, any>();
      empresasOwnerList.forEach(emp => empresasMap.set(emp.id, emp));
      empresasMemberList.forEach(emp => {
        if (emp && !empresasMap.has(emp.id)) {
          empresasMap.set(emp.id, emp);
        }
      });

      const empresasData = Array.from(empresasMap.values());
      logger.debug('✅ Found empresas (owner + member):', empresasData.length);

      // Convert empresas to userEmpresas format for compatibility
      // Use actual role from usuarios_empresa or 'owner' if user is the owner
      const empresas = empresasData || [];
      const userEmpresas = empresas.map(empresa => {
        // Determine role: 'owner' if empresa.owner_id === usuarioId, otherwise use role from usuarios_empresa
        const isOwner = empresa.owner_id === usuarioId;
        const role = isOwner ? 'OWNER' : (empresaRoleMap.get(empresa.id) || 'EMPLEADO');
        
        return {
          id: `temp-${empresa.id}`,
          user_id: userRecord.id, // Use usuarios.id, not auth.users.id
          empresa_id: empresa.id,
          role: role as UserEmpresa['role'],
          created_at: new Date().toISOString(),
          empresa: empresa
        };
      });
      
      // For session restore, try to use the last selected company
      // Check localStorage for last selected empresa
      const lastSelectedEmpresaId = localStorage.getItem('lastSelectedEmpresaId');
      
      let currentEmpresa = null;
      
      if (lastSelectedEmpresaId && userEmpresas.length > 0) {
        // Try to find the last selected company
        const lastSelected = userEmpresas.find(ue => ue.empresa.id === lastSelectedEmpresaId);
        if (lastSelected) {
          currentEmpresa = lastSelected.empresa;
          logger.debug('✅ Restored last selected company from localStorage:', currentEmpresa.nombre);
        } else {
          // Clean up stale localStorage entry - empresa no longer accessible to user
          localStorage.removeItem('lastSelectedEmpresaId');
          logger.debug('⚠️ Stale empresa ID in localStorage, cleared and will use first available empresa');
        }
      }
      
      // CRITICAL SECURITY: Only set first empresa if user actually owns or is a member of it
      // DO NOT auto-assign to empresas the user didn't create or wasn't invited to
      // This prevents new users from being auto-assigned to random empresas
      if (!currentEmpresa && userEmpresas.length > 0) {
        // Only use empresas from userEmpresas (user is owner or member)
        const primeraEmpresaUsuario = userEmpresas[0];
        if (primeraEmpresaUsuario?.empresa) {
          currentEmpresa = primeraEmpresaUsuario.empresa;
          if (currentEmpresa) {
            logger.debug('✅ Using first empresa user is associated with:', currentEmpresa.nombre);
            // Save to localStorage so it persists for next session
            localStorage.setItem('lastSelectedEmpresaId', currentEmpresa.id);
          }
        }
      }
      
      // SECURITY: If user has NO empresas, ensure currentEmpresa is null
      // DO NOT fallback to any empresa - user must create their own
      if (userEmpresas.length === 0) {
        currentEmpresa = null;
        localStorage.removeItem('lastSelectedEmpresaId');
        logger.warn('⚠️ User has no empresas - currentEmpresa set to null. User must create a company.');
      }
      
      // DO NOT auto-assign from empresasData - user must explicitly own or be a member
      // This prevents new users from being auto-assigned to random empresas
      
      // Only show selector if multiple empresas AND no currentEmpresa selected
      // If we have a currentEmpresa, don't show the selector (user can use dropdown instead)
      let showCompanySelector = false;
      if (userEmpresas.length > 1 && !currentEmpresa) {
        showCompanySelector = true;
        logger.debug('🔄 Multiple empresas found but no empresa selected, will show selector');
      } else if (userEmpresas.length > 1 && currentEmpresa) {
        logger.debug('✅ Multiple empresas found, currentEmpresa already selected:', currentEmpresa.nombre);
      }
      
      logger.debug('Final currentEmpresa selection:', {
        currentEmpresa: currentEmpresa ? { id: currentEmpresa.id, nombre: currentEmpresa.nombre } : null,
        userEmpresasCount: userEmpresas.length,
        showCompanySelector
      });

      // CRITICAL: Use sessionUsuario (which has userRecord.id) and add empresa data to it
      // DO NOT create a new usuario object with session?.user?.id
      const usuario: Usuario = {
        ...sessionUsuario, // Use sessionUsuario which has correct userRecord.id
        empresa: currentEmpresa?.nombre || 'Sin Empresa',
        empresaId: currentEmpresa?.id || 'sin-empresa',
        // NEW: Multi-company properties
        userEmpresas: userEmpresas.map(ue => ({
          ...ue,
          empresa: empresaRowToFrontend(ue.empresa),
        })),
        currentEmpresa: currentEmpresa ? empresaRowToFrontend(currentEmpresa) : null,
      };

      logger.debug('✅ Session restored for user:', usuario.email);
      
      const validation = validateUserSetup(usuario);
      if (!validation.isComplete) {
        logger.warn('User setup incomplete:', validation.issues);
      }
      logger.debug('Final state update:', {
        usuarioCurrentEmpresa: usuario.currentEmpresa ? { id: usuario.currentEmpresa.id, nombre: usuario.currentEmpresa.nombre } : null,
        stateCurrentEmpresa: currentEmpresa ? { id: currentEmpresa.id, nombre: currentEmpresa.nombre } : null,
        userEmpresasCount: userEmpresas.length
      });

          set({
            usuario,
            authUser: {
              id: session.user.id,
              email: session.user.email ?? undefined,
              user_metadata: session.user.user_metadata ?? undefined,
            },
            token: session.access_token,
            isAuthenticated: true,
            isLoading: false,
        error: null,
        userSetupValidation: validation,
        userEmpresas: usuario.userEmpresas,
        currentEmpresa: usuario.currentEmpresa,
        showCompanySelector: showCompanySelector, // Only show selector if multiple companies AND no empresa selected
          });

      // Also sync with CompanyStore to keep both stores in sync
      if (currentEmpresa && userEmpresas.length > 0) {
        try {
          const { useCompanyStore } = await import('./useCompanyStore');
          const empresasFrontend = userEmpresas
            .filter(ue => ue.empresa)
            .map(ue => {
              const empresa = ue.empresa!;
              const fe = empresaRowToFrontend(empresa);
              return {
                id: empresa.id,
                nombre: empresa.nombre,
                descripcion: empresa.descripcion || undefined,
                direccion: empresa.direccion || '',
                ciudad: empresa.ciudad || undefined,
                pais: empresa.pais || undefined,
                telefono: empresa.telefono || '',
                email: empresa.email || '',
                slug: empresa.slug || '',
                logo: empresa.logo || empresa.configuracion?.branding?.logo || undefined,
                ownerId: empresa.owner_id || '',
                activa: empresa.activa !== undefined ? empresa.activa : true,
                createdAt: fe.createdAt.toISOString(),
                updatedAt: fe.updatedAt.toISOString()
              };
            });

          const sessionCurrentFe = empresaRowToFrontend(currentEmpresa);
          const currentEmpresaFrontend = currentEmpresa ? {
            id: currentEmpresa.id,
            nombre: currentEmpresa.nombre,
            descripcion: currentEmpresa.descripcion || undefined,
            direccion: currentEmpresa.direccion || '',
            ciudad: currentEmpresa.ciudad || undefined,
            pais: currentEmpresa.pais || undefined,
            telefono: currentEmpresa.telefono || '',
            email: currentEmpresa.email || '',
            slug: currentEmpresa.slug || '',
            logo: currentEmpresa.logo || undefined,
            ownerId: currentEmpresa.owner_id || '',
            activa: currentEmpresa.activa !== undefined ? currentEmpresa.activa : true,
            createdAt: sessionCurrentFe.createdAt.toISOString(),
            updatedAt: sessionCurrentFe.updatedAt.toISOString()
          } : null;

          useCompanyStore.setState({
            empresas: empresasFrontend,
            currentEmpresa: currentEmpresaFrontend,
            loading: false
          });

          logger.debug('✅ CompanyStore synced from AuthStore after checkSession');
        } catch (syncError) {
          logger.warn('⚠️ Error syncing CompanyStore:', syncError);
          // Don't fail checkSession if sync fails
        }
      }
          
        } catch (error) {
          logger.error('❌ Session check failed:', error);
          set({ isLoading: false, isAuthenticated: false });
        }
  },

  // NEW: FETCH USER EMPRESAS
  fetchUserEmpresas: async () => {
    const { usuario } = get();
    if (!usuario || !usuario.email) return;

    set({ isLoadingEmpresas: true });

    try {
      const { supabase } = await import('../lib/supabase');
      
      // IMPORTANT: usuario.id is from auth.users, but empresas.owner_id references usuarios.id
      // So we need to find the usuario record first, then search for empresas using usuarios.id
      
      // Step 1: Find usuario record in usuarios table by email
      const { data: usuarioRecord, error: usuarioError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', usuario.email)
        .maybeSingle();

      if (usuarioError) {
        console.error('❌ Error fetching usuario record:', usuarioError);
        set({ isLoadingEmpresas: false });
        return;
      }

      if (!usuarioRecord) {
        console.warn('⚠️ Usuario not found in usuarios table, no empresas to fetch');
        set({ 
          userEmpresas: [],
          isLoadingEmpresas: false 
        });
        return;
      }

      // Step 2: Search for empresas where user is owner OR member
      const usuarioId = usuarioRecord.id;
      console.log('🔍 Fetching empresas using usuario.id:', usuarioId);
      
      // Get empresas where user is owner
      const { data: empresasOwner, error: ownerError } = await supabase
        .from('empresas')
        .select('id, nombre, descripcion, direccion, ciudad, pais, telefono, email, slug, owner_id, activa, configuracion, created_at, updated_at, emprendedores_de_fe_activo')
        .eq('owner_id', usuarioId);

      if (ownerError) {
        console.error('❌ Error fetching empresas (owner):', ownerError);
      }

      // Get empresas where user is a member via usuarios_empresa
      // First get the empresa IDs from usuarios_empresa (fetch separately to avoid FK relationship requirement)
      const { data: userEmpresasData, error: userEmpresasError } = await supabase
        .from('usuarios_empresa')
        .select('empresa_id, role')
        .eq('usuario_id', usuarioId);

      if (userEmpresasError) {
        console.warn('⚠️ Error fetching empresas (member) - this is OK if user is only owner:', userEmpresasError);
      }

      const empresaRoleMap = new Map<string, string>();
      if (userEmpresasData) {
        userEmpresasData.forEach((ue: { empresa_id?: string; role?: string }) => {
          if (ue.empresa_id && ue.role) {
            empresaRoleMap.set(ue.empresa_id, ue.role);
          }
        });
      }

      // Combine both lists (owner and member empresas)
      const empresasOwnerList = empresasOwner || [];
      let empresasMemberList: any[] = [];
      
      // If we have member empresa IDs, fetch the empresas separately
      if (userEmpresasData && userEmpresasData.length > 0) {
        const memberEmpresaIds = userEmpresasData
          .map(ue => ue.empresa_id)
          .filter((id): id is string => !!id && typeof id === 'string');
        
        if (memberEmpresaIds.length > 0) {
          const { data: memberEmpresas, error: memberEmpresasError } = await supabase
            .from('empresas')
            .select('id, nombre, descripcion, direccion, ciudad, pais, telefono, email, slug, owner_id, activa, configuracion, created_at, updated_at, emprendedores_de_fe_activo')
            .in('id', memberEmpresaIds);
          
          if (!memberEmpresasError && memberEmpresas) {
            empresasMemberList = memberEmpresas;
          }
        }
      }
      
      // Merge and deduplicate by empresa.id
      const empresasMap = new Map<string, any>();
      empresasOwnerList.forEach(emp => empresasMap.set(emp.id, emp));
      empresasMemberList.forEach(emp => {
        if (emp && !empresasMap.has(emp.id)) {
          empresasMap.set(emp.id, emp);
        }
      });

      const empresas = Array.from(empresasMap.values());
      console.log('✅ Found empresas (owner + member):', empresas.length);
      const userEmpresas = empresas.map(empresa => {
        const isOwner = empresa.owner_id === usuarioId;
        const role = isOwner ? 'OWNER' : (empresaRoleMap.get(empresa.id) || 'EMPLEADO');
        return {
          id: `temp-${empresa.id}`,
          user_id: usuario.id,
          empresa_id: empresa.id,
          role: role as UserEmpresa['role'],
          created_at: new Date().toISOString(),
          empresa: empresaRowToFrontend(empresa)
        };
      });

      console.log('✅ Fetched user empresas:', userEmpresas.length);

      set({
        userEmpresas,
        isLoadingEmpresas: false,
      });

      // Update usuario with fresh data
      set({
        usuario: {
          ...usuario,
          userEmpresas: userEmpresas,
        }
      });

      const stateAfter = get();
      if (!stateAfter.currentEmpresa && userEmpresas.length === 1 && userEmpresas[0]?.empresa?.id) {
        await get().switchEmpresa(userEmpresas[0].empresa!.id);
      }

      const refreshed = get();
      if (refreshed.usuario) {
        set({
          userSetupValidation: validateUserSetup({
            ...refreshed.usuario,
            userEmpresas: refreshed.userEmpresas,
            currentEmpresa: refreshed.currentEmpresa,
          }),
        });
      }

    } catch (error) {
      console.error('❌ Error fetching user empresas:', error);
      set({ isLoadingEmpresas: false });
    }
  },

  // NEW: SWITCH EMPRESA
  switchEmpresa: async (empresaId: string) => {
    const { usuario, userEmpresas } = get();
    if (!usuario) {
      logger.error('❌ No usuario found');
      return;
    }

    // Try to find empresa in userEmpresas first
    let empresa = userEmpresas.find(ue => ue.empresa?.id === empresaId)?.empresa;
    
    // If not found in userEmpresas, try to get from CompanyStore
    if (!empresa) {
      try {
        const { useCompanyStore } = await import('./useCompanyStore');
        const companyStore = useCompanyStore.getState();
        const empresaFromStore = companyStore.empresas.find(e => e.id === empresaId);
        if (empresaFromStore) {
          // Convert from CompanyStore format to AuthStore format
          const createdAt = empresaFromStore.createdAt ? new Date(empresaFromStore.createdAt) : new Date();
          const updatedAt = empresaFromStore.updatedAt ? new Date(empresaFromStore.updatedAt) : new Date();
          
          empresa = {
            id: empresaFromStore.id,
            nombre: empresaFromStore.nombre,
            descripcion: empresaFromStore.descripcion || undefined,
            direccion: empresaFromStore.direccion || undefined,
            telefono: empresaFromStore.telefono || undefined,
            email: empresaFromStore.email || '',
            slug: empresaFromStore.slug || undefined,
            logo: empresaFromStore.logo || undefined,
            owner_id: empresaFromStore.ownerId || undefined,
            created_at: createdAt.toISOString(),
            updated_at: updatedAt.toISOString(),
            activa: true, // Default to active
            createdAt,
            updatedAt
          };
          logger.debug('✅ Found empresa in CompanyStore:', empresa.nombre);
        }
      } catch (error) {
        logger.warn('⚠️ Error getting empresa from CompanyStore:', error);
      }
    }

    if (!empresa) {
      logger.error('❌ Empresa not found:', empresaId);
      return;
    }

    const empresaNormalized = empresaRowToFrontend(empresa);

    // Log the switch for audit (optional - table may not exist)
    // This is just for tracking purposes, so we'll catch errors silently
    try {
      const { supabase } = await import('../lib/supabase');
      await supabase
        .from('company_switches')
        .insert({
          user_id: usuario.id,
          from_empresa_id: usuario.currentEmpresa?.id || null,
          to_empresa_id: empresaId,
          switched_at: new Date().toISOString(),
        });
    } catch (error: any) {
      // Silently ignore if table doesn't exist - it's optional for audit purposes
      if (error?.code !== 'PGRST116' && error?.code !== '42P01') {
        logger.warn('⚠️ Could not log company switch (optional audit table):', error.message);
      }
    }

    // Update current empresa
    const updatedUsuario = {
      ...usuario,
      currentEmpresa: empresaNormalized,
      empresa: empresaNormalized.nombre,
      empresaId: empresaNormalized.id,
    };

    set({
      usuario: updatedUsuario,
      currentEmpresa: empresaNormalized,
      showCompanySelector: false, // Hide the selector modal after selection
    });

    // Also update CompanyStore to keep both stores in sync
    try {
      const { useCompanyStore } = await import('./useCompanyStore');
      const companyStore = useCompanyStore.getState();
      const empresaFromStore = companyStore.empresas.find(e => e.id === empresaId);
      if (empresaFromStore) {
        companyStore.setCurrentEmpresa(empresaFromStore);
        logger.debug('✅ Sincronizado con CompanyStore');
      }
    } catch (syncError) {
      logger.warn('⚠️ Error syncing with CompanyStore:', syncError);
      // Don't fail if sync fails
    }

    // Save the selected empresa to localStorage for persistence
    localStorage.setItem('lastSelectedEmpresaId', empresaId);
    logger.debug('✅ Switched to empresa:', empresaNormalized.nombre);

    // Force a page reload to ensure all components refresh with new empresa data
    logger.debug('🔄 Reloading page to refresh all data...');
    window.location.reload();
  },

  // NEW: SET CURRENT EMPRESA
  setCurrentEmpresa: (empresa: Empresa) => {
    const { usuario } = get();
    if (!usuario) return;

    const updatedUsuario = {
      ...usuario,
      currentEmpresa: empresa,
      empresa: empresa.nombre,
      empresaId: empresa.id,
    };

    // Save to localStorage for persistence across sessions
    localStorage.setItem('lastSelectedEmpresaId', empresa.id);

    set({
      usuario: updatedUsuario,
      currentEmpresa: empresa,
    });

    logger.debug('✅ Current empresa set to:', empresa.nombre);
  },

  /** Update current usuario with empresa_id and rol_id (Admin) after first empresa is created. */
  createUserAfterEmpresaSetup: async (empresaId: string, nombreUsuario?: string): Promise<Usuario | null> => {
    const syncAfterSetup = async () => {
      await get().fetchUserEmpresas();
      await get().switchEmpresa(empresaId);
      const refreshed = get();
      if (refreshed.usuario) {
        set({
          userSetupValidation: validateUserSetup({
            ...refreshed.usuario,
            userEmpresas: refreshed.userEmpresas,
            currentEmpresa: refreshed.currentEmpresa,
          }),
        });
      }
    };

    try {
      const { usuario, authUser } = get();
      const { supabase } = await import('../lib/supabase');

      // 1. Get Admin role for this empresa (trigger creates default roles on empresa insert)
      const { data: adminRole, error: roleError } = await supabase
        .from('roles')
        .select('id, nombre')
        .eq('empresa_id', empresaId)
        .eq('nombre', 'Admin')
        .single();

      if (roleError || !adminRole) {
        const { data: anyRole } = await supabase
          .from('roles')
          .select('id, nombre')
          .eq('empresa_id', empresaId)
          .limit(1)
          .single();
        if (!anyRole) {
          logger.error('❌ No roles found for empresa:', empresaId);
          return null;
        }
        // Use first available role
        if (usuario) {
          const { data: updated, error: updateErr } = await supabase
            .from('usuarios')
            .update({
              empresa_id: empresaId,
              rol_id: anyRole.id,
              rol: anyRole.nombre,
              updated_at: new Date().toISOString(),
            })
            .eq('id', usuario.id)
            .select()
            .single();
          if (updateErr) {
            logger.error('❌ Error updating usuario after empresa setup:', updateErr);
            return null;
          }
          set({ usuario: rowToUsuario(updated) });
          await syncAfterSetup();
          return rowToUsuario(updated);
        }
        if (authUser) {
          const { data: newUser, error: createErr } = await supabase
            .from('usuarios')
            .insert({
              auth_id: authUser.id,
              email: authUser.email ?? '',
              nombre: nombreUsuario ?? authUser.email?.split('@')[0] ?? 'Usuario',
              rol: anyRole.nombre,
              rol_id: anyRole.id,
              empresa_id: empresaId,
              activo: true,
            })
            .select()
            .single();
          if (createErr || !newUser) {
            logger.error('❌ Error creating usuario after empresa setup:', createErr);
            return null;
          }
          set({ usuario: rowToUsuario(newUser) });
          await syncAfterSetup();
          return rowToUsuario(newUser);
        }
        return null;
      }

      if (usuario) {
        const { data: updated, error: updateErr } = await supabase
          .from('usuarios')
          .update({
            empresa_id: empresaId,
            rol_id: adminRole.id,
            rol: adminRole.nombre,
            updated_at: new Date().toISOString(),
          })
          .eq('id', usuario.id)
          .select()
          .single();
        if (updateErr) {
          logger.error('❌ Error updating usuario after empresa setup:', updateErr);
          return null;
        }
        set({ usuario: rowToUsuario(updated) });
        await syncAfterSetup();
        return rowToUsuario(updated);
      }

      if (authUser) {
        const { data: newUser, error: createErr } = await supabase
          .from('usuarios')
          .insert({
            auth_id: authUser.id,
            email: authUser.email ?? '',
            nombre: nombreUsuario ?? authUser.email?.split('@')[0] ?? 'Usuario',
            rol: adminRole.nombre,
            rol_id: adminRole.id,
            empresa_id: empresaId,
            activo: true,
          })
          .select()
          .single();
        if (createErr || !newUser) {
          logger.error('❌ Error creating usuario after empresa setup:', createErr);
          return null;
        }
        set({ usuario: rowToUsuario(newUser) });
        await syncAfterSetup();
        return rowToUsuario(newUser);
      }

      return null;
    } catch (err) {
      logger.error('❌ createUserAfterEmpresaSetup:', err);
      return null;
    }
  },

  // NEW: CREATE NEW EMPRESA
  createNewEmpresa: async (empresaData: Partial<Empresa>) => {
    const { usuario } = get();
    if (!usuario) return null as any;

    try {
      const { supabase } = await import('../lib/supabase');
      
      // Import country helpers
      const { getCiudadDefaultPorPais } = await import('../utils/constants');
      
      // Use selected country or default to CO; set default city based on country
      const paisCode = (empresaData as any).pais || 'CO';
      const ciudad = (empresaData as any).ciudad || getCiudadDefaultPorPais(paisCode);
      
      const newEmpresaData = {
        nombre: empresaData.nombre || 'Nueva Empresa',
        email: empresaData.email || usuario.email,
        telefono: (empresaData as any).telefono || '',
        planSuscripcion: 'BASICO',
        pais: paisCode,
        ciudad: ciudad,
        direccion: (empresaData as any).direccion || '',
        owner_id: usuario.id,
        activa: true,
        tieneTiendaOnline: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: empresaCreated, error: empresaError } = await supabase
        .from('empresas')
        .insert(newEmpresaData)
        .select()
        .single();

      if (empresaError) {
        console.error('❌ Failed to create empresa:', empresaError);
        throw new Error(`Error creating company: ${empresaError.message}`);
      }

      // Create user-empresa relationship
      const { error: userEmpresaError } = await supabase
        .from('usuarios_empresa')
        .insert({
          usuario_id: usuario.id,
          empresa_id: empresaCreated.id,
          role: 'OWNER',
        });

      if (userEmpresaError) {
        console.log('⚠️ RLS policy blocked usuarios_empresa creation, but empresa was created successfully');
        console.log('ℹ️ The empresa will be accessible via the fallback mechanism');
        // Don't throw error - the empresa is still valid and accessible
        // The fallback mechanism in fetchUserEmpresas will handle this
      }

      // Refresh user empresas
      await get().fetchUserEmpresas();

      console.log('✅ New empresa created:', empresaCreated.nombre);
      return empresaCreated;

    } catch (error) {
      console.error('❌ Error creating new empresa:', error);
      throw error;
    }
  },

  // Existing methods (unchanged)
  logout: async () => {
    console.log('🚪 Logging out...');

    try {
      const { supabase } = await import('../lib/supabase');
      await supabase.auth.signOut();
    } catch (error) {
      console.error('❌ Logout error:', error);
    }

    set({
      usuario: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      registrationEmailPendingVerification: null,
      authResendHintEmail: null,
      userSetupValidation: null,
      userEmpresas: [],
      currentEmpresa: null,
      showCompanySelector: false,
      authUser: null,
      currentStoreGroup: null,
      userStoreGroups: [],
      availableEmpresas: [],
      isExecutiveView: false,
      selectedEmpresas: [],
      globalMetrics: null,
      storePerformance: [],
      isLoadingMetrics: false,
    });

    try {
      const { useCompanyStore } = await import('./useCompanyStore');
      const { useProductosStore } = await import('./useProductosStore');
      const { useClientesStore } = await import('./useClientesStore');
      const { useFacturasStore } = await import('./useFacturasStore');
      const { useDashboardStore } = await import('./useDashboardStore');
      const { useSubscriptionsStore } = await import('./useSubscriptionsStore');
      const { useVentasStore } = await import('./useVentasStore');

      useCompanyStore.getState().reset?.();
      useProductosStore.getState().clearState?.();
      useClientesStore.getState().reset?.();
      useFacturasStore.getState().reset?.();
      useDashboardStore.getState().reset?.();
      useSubscriptionsStore.getState().reset?.();
      useVentasStore.getState().reset?.();
    } catch (e) {
      console.warn('Logout: error resetting stores', e);
    }

    try {
      localStorage.removeItem('lastSelectedEmpresaId');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('clientelo') || k.startsWith('supabase') || k.includes('empresa'))) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      sessionStorage.clear();
    } catch (e) {
      console.warn('Logout: error clearing storage', e);
    }

    console.log('✅ Logout successful');
    window.location.href = '/login';
  },

  setUsuario: (usuario: Usuario) => {
    set({ usuario });
  },

  setToken: (token: string) => {
    set({ token });
  },

  clearError: () => {
    set({ error: null, authResendHintEmail: null });
  },

  dismissRegistrationPending: () => {
    set({ registrationEmailPendingVerification: null });
  },

  resendSignupEmail: async (email: string) => {
    const normalizedEmail = normalizeLoginEmail(email);
    if (!normalizedEmail) {
      throw new Error('Correo inválido.');
    }
    const { supabase } = await import('../lib/supabase');
    const emailRedirectTo =
      typeof window !== 'undefined' ? `${window.location.origin}/login` : undefined;
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizedEmail,
      options: emailRedirectTo ? { emailRedirectTo } : {},
    });
    if (error) {
      throw new Error(
        mapSignupAuthError(error.message) ||
          'No se pudo reenviar el correo. Revisa la configuración de correo en Supabase.'
      );
    }
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  validateUserSetup: () => {
    const { usuario } = get();
    if (usuario) {
      const validation = validateUserSetup(usuario);
      set({ userSetupValidation: validation });
      return validation;
    }
    return { isComplete: false, issues: ['no_user'] };
  },

  // NEW: Simple executive dashboard methods
  fetchStoreGroups: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: usuarioRow } = await supabase
        .from('usuarios')
        .select('id')
        .eq('auth_id', user.id)
        .maybeSingle();

      const usuarioId = usuarioRow?.id;
      if (!usuarioId) return;

      const { data: userEmpresas, error } = await supabase
        .from('usuarios_empresa')
        .select(`
          *,
          empresa:empresas(
            *,
            parent_empresa:empresas!parent_empresa_id(*),
            child_empresas:empresas!parent_empresa_id(*)
          )
        `)
        .eq('usuario_id', usuarioId);

      if (error) throw error;

      // Group empresas by parent (store groups)
      const storeGroups: StoreGroup[] = [];
      const processedEmpresas = new Set<string>();

      userEmpresas?.forEach(userEmpresa => {
        const empresa = userEmpresa.empresa;
        if (!empresa || processedEmpresas.has(empresa.id)) return;

        // If it's a parent empresa (no parent_empresa_id)
        if (!empresa.parent_empresa_id) {
          const childEmpresas = userEmpresas
            .filter(ue => ue.empresa.parent_empresa_id === empresa.id)
            .map(ue => ue.empresa);

          storeGroups.push({
            id: empresa.id,
            nombre: empresa.nombre,
            location_name: empresa.location_name,
            parent_empresa_id: empresa.parent_empresa_id,
            child_empresas: childEmpresas,
            total_stores: childEmpresas.length + 1
          });

          processedEmpresas.add(empresa.id);
          childEmpresas.forEach(child => processedEmpresas.add(child.id));
        }
      });

      set({ 
        userStoreGroups: storeGroups,
        availableEmpresas: userEmpresas?.map(ue => ue.empresa) || [],
        ...(storeGroups.length > 0 || (userEmpresas?.length ?? 0) > 1
          ? { currentAccessLevel: 'executive' as const, isExecutiveView: true }
          : {}),
      });

      if (storeGroups.length > 0) {
        set({ currentStoreGroup: storeGroups[0] });
      }
    } catch (error) {
      console.error('Error fetching store groups:', error);
    }
  },

  switchToExecutiveView: () => {
    set({ 
      isExecutiveView: true,
      currentAccessLevel: 'executive'
    });
  },

  switchToStoreView: (empresaId: string) => {
    const { availableEmpresas } = get();
    const empresa = availableEmpresas.find(e => e.id === empresaId);
    
    if (empresa) {
      set({ 
        isExecutiveView: false,
        currentAccessLevel: 'store',
        selectedEmpresas: [empresaId]
      });
    }
  },

  setCurrentStoreGroup: (storeGroup: StoreGroup) => {
    set({ 
      currentStoreGroup: storeGroup,
      availableEmpresas: storeGroup.child_empresas || []
    });
  },

  setSelectedEmpresas: (empresaIds: string[]) => {
    set({ selectedEmpresas: empresaIds });
  },

  setSelectedTimeRange: (timeRange: string) => {
    set({ selectedTimeRange: timeRange });
  },

  fetchGlobalMetrics: async (empresaIds: string[], timeRange: string) => {
    set({ isLoadingMetrics: true });
    
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = parseInt(timeRange.replace('d', '')) || 30;
      startDate.setDate(endDate.getDate() - days);

      // Fetch consolidated metrics across selected empresas
      const { data: salesData, error: salesError } = await supabase
        .from('ventas')
        .select(`
          total,
          fecha,
          empresa_id
        `)
        .in('empresa_id', empresaIds)
        .gte('fecha', startDate.toISOString())
        .lte('fecha', endDate.toISOString());

      if (salesError) throw salesError;

      // Calculate metrics
      const totalRevenue = salesData?.reduce((sum, sale) => sum + (sale.total || 0), 0) || 0;
      const totalSales = salesData?.length || 0;
      const averageTicket = totalSales > 0 ? totalRevenue / totalSales : 0;

      set({ 
        globalMetrics: {
          totalRevenue,
          totalSales,
          totalCustomers: 0, // Will be calculated separately
          totalProducts: 0,  // Will be calculated separately
          averageTicket,
          topPerformingStore: '',
          storeCount: empresaIds.length,
          activeStores: empresaIds.length
        },
        isLoadingMetrics: false
      });

    } catch (error) {
      console.error('Error fetching global metrics:', error);
      set({ isLoadingMetrics: false });
    }
  },

  fetchStorePerformance: async (empresaIds: string[], timeRange: string) => {
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = parseInt(timeRange.replace('d', '')) || 30;
      startDate.setDate(endDate.getDate() - days);

      // Fetch store performance data
      const { data: performanceData, error } = await supabase
        .from('ventas')
        .select(`
          total,
          fecha,
          empresa_id
        `)
        .in('empresa_id', empresaIds)
        .gte('fecha', startDate.toISOString())
        .lte('fecha', endDate.toISOString());

      if (error) throw error;

      // Get empresa names for display
      const { data: empresasData } = await supabase
        .from('empresas')
        .select('id, nombre, location_name')
        .in('id', empresaIds);

      const empresaMap = new Map(empresasData?.map(e => [e.id, e]) || []);

      // Calculate performance by empresa
      const storePerformance = new Map();
      performanceData?.forEach(venta => {
        const empresaId = venta.empresa_id;
        if (empresaId) {
          const empresa = empresaMap.get(empresaId);
          const current = storePerformance.get(empresaId) || { 
            revenue: 0, 
            sales: 0, 
            empresaName: empresa?.nombre || 'Unknown Store',
            locationName: empresa?.location_name
          };
          storePerformance.set(empresaId, {
            ...current,
            revenue: current.revenue + venta.total,
            sales: current.sales + 1
          });
        }
      });

      const performanceArray = Array.from(storePerformance.entries()).map(([empresaId, data]) => ({
        empresaId,
        empresaName: data.empresaName,
        locationName: data.locationName,
        revenue: data.revenue,
        sales: data.sales,
        customers: 0, // Will be calculated separately
        averageTicket: data.sales > 0 ? data.revenue / data.sales : 0,
        lastUpdated: new Date().toISOString()
      }));

      set({ storePerformance: performanceArray });

    } catch (error) {
      console.error('Error fetching store performance:', error);
    }
  },

  requestPasswordReset: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/reset-password`,
    });
    if (error) throw error;
    logger.debug('Password reset email sent to:', email);
  },

  resetPassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    await supabase.auth.signOut();
    logger.debug('Password updated successfully');
  },

  verifyRecoveryOtp: async (tokenHash: string) => {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.verifyOtp({
      type: 'recovery',
      token_hash: tokenHash,
    });
    if (error) throw error;
    logger.debug('Recovery OTP verified');
  },
}));
